import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
} from '../auth-data/auth-http.service';
import { IResponseInfo } from '../http/http.data';
import { RendererService } from '../renderer/renderer.service';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { LoggerService } from '../shared/services/logger/logger.service';
import { StatementContent } from './models/statement-content.model';
import { StatementInfo } from './models/statement-info.model';
import { StatementMetrics } from './models/statement-metrics.model';
import { Statement } from './models/statement.model';
import {
  IStatementData,
  IStatementInfoParams,
  IStatementMetricParams,
  STATEMENT_ACCEPT_VERSION,
  STATEMENT_METRICS_ACCEPT_VERSION,
} from './statement-data.data';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';

@Injectable()
export class StatementDataService {
  private _statementsUrl = '/statements';
  private _statementsMetricsUrl = '/statements/metrics';
  private _statementsBaseQueryParam: { [queryParam: string]: string } = {};
  private _statementMetricsHeaders: any = {
    [ACCEPT_VERSION_HEADER]: STATEMENT_METRICS_ACCEPT_VERSION,
  };
  private _statementHeaders: any = {
    [ACCEPT_VERSION_HEADER]: STATEMENT_ACCEPT_VERSION,
  };

  constructor(
    private _authHttpService: AuthHttpService,
    private _loggerService: LoggerService,
    private _settingsDataStore: SettingsDataStore,
    private _rendererService: RendererService
  ) {
    this._settingsDataStore.settings$
      .pipe(
        filter((x) => !!x),
        map(
          (settings: Settings) => settings.documentPresetSettings.currencyCode
        ),
        distinctUntilChanged()
      )
      .subscribe(
        (currencyCode: string) =>
          (this._statementsBaseQueryParam = { currency_code: currencyCode })
      );
  }

  getStatementMetrics(
    params: IStatementMetricParams
  ): Observable<StatementMetrics> {
    const url = this._statementsMetricsUrl;

    this._loggerService.log(
      'getting statements metrics from network @ url:',
      url
    );

    return this._authHttpService
      .get(url, params, this._statementMetricsHeaders)
      .pipe(
        map(
          ({ body, ..._ }: IResponseInfo) =>
            new StatementMetrics(body['statement_metrics'])
        ),
        take(1)
      );
  }

  getStatementInfo(params: IStatementInfoParams): Observable<StatementInfo> {
    const url = this._statementsUrl;

    this._loggerService.log(
      'fetching statements content from network @ url',
      url
    );

    return this._authHttpService.get(url, params, this._statementHeaders).pipe(
      map(({ body, ..._ }: IResponseInfo) => {
        const statementContent: StatementContent = new StatementContent(
          body['statement']
        );

        return new StatementInfo(body).setIn(
          [StatementInfo.KEYS.STATEMENT, Statement.KEYS.CONTENT],
          statementContent
        ) as StatementInfo;
      }),
      take(1)
    );
  }

  sendStatement(
    clientId: string,
    payload: IStatementData
  ): Observable<IResponseInfo> {
    return this._rendererService.sendStatement(clientId, payload);
  }
}
